import { ReactNode } from 'react';
import { BusinessAddon } from '@/apis/business/addons/types';
import AddonsPrioritySupportIcon from '@/assets/icons/AddonsPrioritySupportIcon';
import AddonsSMSIcon from '@/assets/icons/AddonsSMSIcon';
import AddonsA2pIcon from '@/assets/icons/AddonsA2pIcon';
import AddonsWorkflowIcon from '@/assets/icons/AddonsWorkflowIcon';
import AddonsConcurrencyIcon from '@/assets/icons/AddonsConcurrencyIcon';
import { MdOutlineAutoAwesome } from 'react-icons/md';
import dayjs from 'dayjs';

const DISPLAY_TITLES: Record<string, string> = {
  priority_support: 'Priority Support',
  sms_hosting: 'SMS on Existing Business Number',
  a2p_10dlc: 'A2P 10DLC Registration',
  ai_workflow: 'AI Workflows',
  concurrency_limit: 'Concurrency Limit'
};

const SHORT_BLURBS: Record<string, string> = {
  priority_support:
    'Priority handling on every support request, with immediate support during non-working hours and weekends. Billed per location.',
  sms_hosting: 'Enable SMS on your existing business number without changing your voice provider.',
  a2p_10dlc: 'A2P 10DLC registration is mandatory for U.S. business messaging on 10-digit long codes.',
  ai_workflow: 'AI-powered workflows that handle repetitive admin tasks across all locations.',
  concurrency_limit:
    'Let your AI Agent handle more simultaneous calls during peak times without callers reaching a busy line.'
};

export const ADDON_DISPLAY_ORDER = ['priority_support', 'sms_hosting', 'a2p_10dlc', 'ai_workflow', 'concurrency_limit'];

/** Shared width for activate / deactivate / terms / eligibility drawers. */
export const ADDON_DRAWER_WIDTH = 520;

function money(value: number | string | undefined): string {
  const amount = Number(value) || 0;
  if (Number.isInteger(amount)) return `$${amount}`;
  return `$${amount.toFixed(2)}`;
}

export function num(value: number | string | undefined): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function getAddonDisplayTitle(addon: BusinessAddon): string {
  return DISPLAY_TITLES[addon.code] || addon.name;
}

export function getAddonShortBlurb(addon: BusinessAddon): string {
  return SHORT_BLURBS[addon.code] || '';
}

export function formatCommitmentLabel(months?: number | null): string {
  const value = Number(months) || 0;
  if (value <= 0) return 'No Commitment';
  return `${value} Months`;
}

export function formatCommitmentFull(months?: number | null): string {
  const value = Number(months) || 0;
  if (value <= 0) return 'No Commitment';
  return `${value} Months Commitment`;
}

export function getAddonIcon(code: string): ReactNode {
  switch (code) {
    case 'priority_support':
      return <AddonsPrioritySupportIcon />;
    case 'sms_hosting':
      return <AddonsSMSIcon />;
    case 'a2p_10dlc':
      return <AddonsA2pIcon />;
    case 'ai_workflow':
      return <AddonsWorkflowIcon />;
    case 'concurrency_limit':
      return <AddonsConcurrencyIcon />;
    default:
      return <MdOutlineAutoAwesome size={28} />;
  }
}

/** Structured price badge parts for business add-on cards. */
export type AddonPriceBadgePiece =
  | { type: 'segment'; amount: string; unit: string; emphasizeAmount?: boolean }
  | { type: 'text'; value: string }
  | { type: 'dot' };

function segment(amount: string, unit: string, emphasizeAmount = true): AddonPriceBadgePiece {
  return { type: 'segment', amount, unit, emphasizeAmount };
}

export function getAddonPriceBadgePieces(addon: BusinessAddon): AddonPriceBadgePiece[] {
  const p = addon.prices || {};

  switch (addon.code) {
    case 'priority_support': {
      const setup = num(p.one_time_setup_fee);
      const monthly = segment(money(p.price_per_location), '/location/month');
      if (setup > 0) {
        return [segment(money(setup), ' one-time + '), monthly];
      }
      return [monthly];
    }
    case 'sms_hosting':
      return [segment(money(p.price_per_hosted_number), '/hosted number/month')];
    case 'a2p_10dlc':
      return [
        segment(money(p.one_time_setup_fee), ' one-time + from '),
        segment(money(p.low_volume_mixed_campaign), '/campaign/month')
      ];
    case 'ai_workflow':
      // Only the monthly price is emphasized; per-run amount stays normal weight.
      return [
        segment(money(p.monthly_price_all_locations), '/month + '),
        segment(money(p.price_per_additional_run), '/run', false)
      ];
    case 'concurrency_limit': {
      const setup = num(p.one_time_setup_fee);
      const monthly = segment(money(p.price_per_concurrency), '/concurrency/month');
      if (setup > 0) {
        return [segment(money(setup), '/one-time + '), monthly];
      }
      return [monthly];
    }
    default:
      return [];
  }
}

/** Billing page Active Add-Ons cards — badge copy matches billing mockups. */
export function getBillingAddonPriceBadgePieces(
  addon: BusinessAddon,
  options?: { includedRuns?: number; isProrated?: boolean }
): AddonPriceBadgePiece[] {
  const p = addon.prices || {};

  switch (addon.code) {
    case 'priority_support':
      return [segment(money(p.price_per_location), '/location/month')];
    case 'sms_hosting':
      return [segment(money(p.price_per_hosted_number), '/hosted number/month')];
    case 'a2p_10dlc':
      return [
        segment(money(p.one_time_setup_fee), ' one-time'),
        { type: 'dot' },
        segment(money(p.mixed_campaign_per_month), ' mixed or'),
        { type: 'dot' },
        segment(money(p.low_volume_mixed_campaign), ' low-volume/per campaign/month')
      ];
    case 'ai_workflow': {
      const included = options?.includedRuns != null ? options.includedRuns : num(p.included_runs_per_month);
      const runsLabel = options?.isProrated
        ? `${included.toLocaleString()} runs included this cycle`
        : `${included.toLocaleString()} runs included`;

      return [
        segment(money(p.monthly_price_all_locations), '/month'),
        { type: 'dot' },
        { type: 'text', value: runsLabel },
        { type: 'dot' },
        segment(money(p.price_per_additional_run), ' per extra run', false)
      ];
    }
    case 'concurrency_limit':
      return [segment(money(p.price_per_concurrency), '/concurrent call/month')];
    default:
      return getAddonPriceBadgePieces(addon);
  }
}

/** Card price line matching business mockups. */
export function formatCardPriceSummary(addon: BusinessAddon): string {
  return getAddonPriceBadgePieces(addon)
    .map((piece) => {
      if (piece.type === 'dot') return ' · ';
      if (piece.type === 'text') return piece.value;
      return `${piece.amount}${piece.unit}`;
    })
    .join('');
}

export function isAddonActive(addon: BusinessAddon): boolean {
  const status = addon.subscription?.status;
  return Boolean(status && status !== 'cancelled' && status !== 'pending_payment');
}

export function isAddonPendingCancellation(addon: BusinessAddon): boolean {
  return addon.subscription?.status === 'pending_cancellation';
}

export function formatDisplayDate(value?: string | null): string {
  if (!value) return '—';
  const date = dayjs(value);
  if (!date.isValid()) return '—';
  return date.format('D MMM YYYY');
}

export function sortAddonsForDisplay(addons: BusinessAddon[]): BusinessAddon[] {
  return [...addons].sort((a, b) => {
    const ai = ADDON_DISPLAY_ORDER.indexOf(a.code);
    const bi = ADDON_DISPLAY_ORDER.indexOf(b.code);
    const aRank = ai === -1 ? ADDON_DISPLAY_ORDER.length : ai;
    const bRank = bi === -1 ? ADDON_DISPLAY_ORDER.length : bi;
    if (aRank !== bRank) return aRank - bRank;
    return a.name.localeCompare(b.name);
  });
}

export function getDueTodayAmount(addon: BusinessAddon): number {
  if (addon.activation_charge?.due_today != null) {
    return num(addon.activation_charge.due_today);
  }

  const p = addon.prices || {};

  switch (addon.code) {
    case 'priority_support':
    case 'ai_workflow':
    case 'concurrency_limit':
      // Recurring is billed with the monthly invoice (prorated), not at activate.
      return num(p.one_time_setup_fee);
    case 'sms_hosting':
      return num(p.one_time_setup_fee);
    case 'a2p_10dlc':
      return num(p.one_time_setup_fee);
    default:
      return num(p.one_time_setup_fee);
  }
}
